import { Component, type ErrorInfo, type ReactNode } from 'react';

/** هل الخطأ ناتج عن ملف صفحة قديم بعد نشر تحديث (تغيّرت أسماء الملفات المُجزّأة)؟ */
function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name} ${error.message}` : String(error);
  return /ChunkLoadError|Loading chunk|dynamically imported module|Importing a module script failed|error loading dynamically/i.test(
    message,
  );
}

const RELOAD_FLAG = 'sarlink:chunk-reload';

interface Props {
  children: ReactNode;
  /** تغيّره يمسح الخطأ (مثل مسار الصفحة) — فالتنقّل لصفحة أخرى يستعيد العمل */
  resetKey?: string;
  /** عرض مضمّن داخل التخطيط بدل ملء الشاشة */
  inline?: boolean;
}

interface State {
  error: Error | null;
}

/**
 * ★ حاجز الأخطاء: يلتقط أي انهيار أثناء عرض الصفحة ويعرض رسالة واضحة بدل شاشة بيضاء.
 * وعند فشل تحميل ملف صفحة بعد نشر تحديث، يعيد تحميل التطبيق تلقائياً مرة واحدة.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('خطأ في الواجهة:', error, info.componentStack);
    if (isStaleChunkError(error)) {
      try {
        // إعادة تحميل واحدة فقط لتفادي حلقة لا نهائية
        if (!sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.setItem(RELOAD_FLAG, '1');
          window.location.reload();
        }
      } catch {
        window.location.reload();
      }
    }
  }

  override componentDidUpdate(prevProps: Props): void {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private reset = () => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      /* تخزين الجلسة غير متاح */
    }
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const stale = isStaleChunkError(error);
    const wrapper = this.props.inline
      ? 'grid place-items-center px-4 py-16'
      : 'grid min-h-full place-items-center bg-slate-100 px-4 py-16 dark:bg-slate-950';

    return (
      <div className={wrapper} role="alert">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-amber-100 text-2xl dark:bg-amber-950">
            ⚠️
          </div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">
            {stale ? 'تم تحديث النظام' : 'حدث خطأ غير متوقع في هذه الصفحة'}
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {stale
              ? 'نُشرت نسخة أحدث من النظام. أعد تحميل الصفحة للمتابعة.'
              : 'لم تُفقد أي بيانات. جرّب مرة أخرى، وإن تكرر الخطأ أبلغ سوبر أدمن.'}
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <button type="button" className="btn-primary" onClick={() => window.location.reload()}>
              إعادة التحميل
            </button>
            {!stale ? (
              <button type="button" className="btn-ghost" onClick={this.reset}>
                المحاولة مرة أخرى
              </button>
            ) : null}
            <a href="/" className="btn-ghost">
              الصفحة الرئيسية
            </a>
          </div>
          {!stale ? (
            <details className="mt-4 text-start text-xs text-slate-400">
              <summary className="cursor-pointer">تفاصيل تقنية</summary>
              <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap" dir="ltr">
                {error.message}
              </pre>
            </details>
          ) : null}
        </div>
      </div>
    );
  }
}
